import { useEffect, useState, useCallback } from "react";
import {
    Plus,
    Pencil,
    Trash2,
    Search,
    Loader2,
    Inbox as InboxIcon,
    Eye,
} from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import ImageUpload from "@/components/admin/ImageUpload";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const arrayToText = (v) => (Array.isArray(v) ? v.join("\n") : "");
const textToArray = (t) =>
    (t || "")
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);

function buildInitial(fields, item) {
    const data = {};
    for (const f of fields) {
        if (item && item[f.name] !== undefined) {
            data[f.name] = item[f.name];
        } else if (f.default !== undefined) {
            data[f.name] = typeof f.default === "function" ? f.default() : f.default;
        } else {
            switch (f.type) {
                case "switch":
                    data[f.name] = false;
                    break;
                case "number":
                    data[f.name] = 0;
                    break;
                case "tags":
                case "process_steps":
                case "images":
                    data[f.name] = [];
                    break;
                default:
                    data[f.name] = "";
            }
        }
    }
    return data;
}

// ---------------------------------------------------------------------------
// Field renderer
// ---------------------------------------------------------------------------
function FieldRenderer({ field, value, onChange, testidPrefix }) {
    const setVal = (v) => onChange(field.name, v);
    const tid = `${testidPrefix}-field-${field.name}`;

    switch (field.type) {
        case "text":
        case "email":
            return (
                <Input
                    id={field.name}
                    name={field.name}
                    type={field.type === "email" ? "email" : "text"}
                    value={value || ""}
                    onChange={(e) => setVal(e.target.value)}
                    placeholder={field.placeholder}
                    data-testid={tid}
                />
            );
        case "number":
            return (
                <Input
                    id={field.name}
                    name={field.name}
                    type="number"
                    value={value ?? 0}
                    onChange={(e) => setVal(Number(e.target.value))}
                    data-testid={tid}
                />
            );
        case "textarea":
            return (
                <Textarea
                    id={field.name}
                    name={field.name}
                    value={value || ""}
                    onChange={(e) => setVal(e.target.value)}
                    placeholder={field.placeholder}
                    rows={field.rows || 4}
                    data-testid={tid}
                />
            );
        case "markdown":
            return (
                <Tabs defaultValue="edit">
                    <TabsList>
                        <TabsTrigger value="edit">Edit</TabsTrigger>
                        <TabsTrigger value="preview">Preview</TabsTrigger>
                    </TabsList>
                    <TabsContent value="edit">
                        <Textarea
                            id={field.name}
                            name={field.name}
                            value={value || ""}
                            onChange={(e) => setVal(e.target.value)}
                            placeholder={field.placeholder}
                            rows={field.rows || 12}
                            className="font-mono text-sm"
                            data-testid={tid}
                        />
                    </TabsContent>
                    <TabsContent value="preview">
                        <div className="prose prose-slate max-w-none border border-slate-200 rounded-md p-4 bg-slate-50 min-h-[300px]">
                            <ReactMarkdown>
                                {value || "_Nothing to preview yet._"}
                            </ReactMarkdown>
                        </div>
                    </TabsContent>
                </Tabs>
            );
        case "switch":
            return (
                <div className="flex items-center gap-3">
                    <Switch
                        id={field.name}
                        checked={!!value}
                        onCheckedChange={setVal}
                        data-testid={tid}
                    />
                    <span className="text-sm text-slate-600">
                        {field.toggleLabel || (value ? "Yes" : "No")}
                    </span>
                </div>
            );
        case "select":
            return (
                <Select value={value || ""} onValueChange={setVal}>
                    <SelectTrigger data-testid={tid}>
                        <SelectValue placeholder={field.placeholder || "Choose"} />
                    </SelectTrigger>
                    <SelectContent>
                        {field.options?.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );
        case "image":
            return (
                <ImageUpload
                    value={value}
                    onChange={setVal}
                    label=""
                    testid={tid}
                />
            );
        case "tags":
            return (
                <Textarea
                    id={field.name}
                    name={field.name}
                    value={arrayToText(value)}
                    onChange={(e) => setVal(textToArray(e.target.value))}
                    placeholder={field.placeholder || "One per line"}
                    rows={field.rows || 5}
                    className="font-mono text-sm"
                    data-testid={tid}
                />
            );
        case "process_steps":
            return (
                <ProcessStepsEditor
                    value={value || []}
                    onChange={setVal}
                    testid={tid}
                />
            );
        case "images":
            return (
                <ImagesEditor
                    value={value || []}
                    onChange={setVal}
                    testid={tid}
                />
            );
        default:
            return null;
    }
}

function ProcessStepsEditor({ value, onChange, testid }) {
    const update = (i, patch) => {
        const next = [...value];
        next[i] = { ...next[i], ...patch };
        onChange(next);
    };
    const add = () => onChange([...value, { title: "", description: "" }]);
    const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
    return (
        <div className="space-y-3" data-testid={testid}>
            {value.map((step, i) => (
                <div
                    key={i}
                    className="border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50"
                >
                    <Input
                        value={step.title}
                        onChange={(e) => update(i, { title: e.target.value })}
                        placeholder={`Step ${i + 1} title`}
                    />
                    <Textarea
                        value={step.description}
                        onChange={(e) => update(i, { description: e.target.value })}
                        placeholder="Description"
                        rows={2}
                    />
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(i)}
                        className="text-destructive"
                    >
                        Remove step
                    </Button>
                </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={add}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add step
            </Button>
        </div>
    );
}

function ImagesEditor({ value, onChange, testid }) {
    return (
        <div className="space-y-3" data-testid={testid}>
            <div className="flex flex-wrap gap-3">
                {value.map((src, i) => (
                    <div key={i} className="relative">
                        <img
                            src={src}
                            alt={`img-${i}`}
                            className="w-28 h-28 object-cover rounded-md border border-slate-200"
                        />
                        <button
                            type="button"
                            onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                            className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-slate-950 text-white grid place-items-center text-xs"
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>
            <ImageUpload
                value=""
                onChange={(v) => v && onChange([...value, v])}
                label="Add image"
                testid={`${testid}-add`}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// ResourceManager — the main exported component
// ---------------------------------------------------------------------------
export default function ResourceManager({
    title,
    description,
    endpoint, // e.g. "services"
    columns, // [{ key, label, render?(row) }]
    fields, // field defs
    testidPrefix,
    searchKeys = ["title", "name", "question", "client_name"],
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null | item
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [search, setSearch] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/${endpoint}`);
            setItems(data);
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setLoading(false);
        }
    }, [endpoint]);

    useEffect(() => {
        load();
    }, [load]);

    const openNew = () => {
        setEditing(null);
        setFormData(buildInitial(fields, null));
        setSheetOpen(true);
    };

    const openEdit = (item) => {
        setEditing(item);
        setFormData(buildInitial(fields, item));
        setSheetOpen(true);
    };

    const setField = (name, value) =>
        setFormData((prev) => ({ ...prev, [name]: value }));

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        try {
            const payload = { ...formData };
            if (editing) {
                await api.patch(`/admin/${endpoint}/${editing.id}`, payload);
                toast.success("Saved");
            } else {
                await api.post(`/admin/${endpoint}`, payload);
                toast.success("Created");
            }
            setSheetOpen(false);
            load();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/admin/${endpoint}/${deleteTarget.id}`);
            toast.success("Deleted");
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const filtered = items.filter((item) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return searchKeys.some((k) => String(item[k] || "").toLowerCase().includes(q));
    });

    return (
        <div className="space-y-6" data-testid={`${testidPrefix}-page`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow">Manage</div>
                    <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tight text-slate-950 mt-1">
                        {title}
                    </h1>
                    {description && (
                        <p className="text-sm text-slate-500 mt-2 max-w-xl">
                            {description}
                        </p>
                    )}
                </div>
                <Button
                    onClick={openNew}
                    className="bg-slate-950 hover:bg-slate-800 text-white"
                    data-testid={`${testidPrefix}-new-button`}
                >
                    <Plus className="w-4 h-4 mr-2" /> Add new
                </Button>
            </div>

            <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                        data-testid={`${testidPrefix}-search`}
                    />
                </div>
                <div className="text-xs text-slate-500">
                    {filtered.length} item{filtered.length === 1 ? "" : "s"}
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                {loading ? (
                    <div className="p-12 grid place-items-center text-slate-400">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center" data-testid={`${testidPrefix}-empty`}>
                        <div className="w-12 h-12 rounded-full bg-slate-100 grid place-items-center mx-auto">
                            <InboxIcon className="w-5 h-5 text-slate-400" />
                        </div>
                        <p className="text-sm text-slate-500 mt-3">
                            No items yet. Click <strong>Add new</strong> to create your
                            first one.
                        </p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50">
                                {columns.map((c) => (
                                    <TableHead key={c.key}>{c.label}</TableHead>
                                ))}
                                <TableHead className="w-[120px] text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.map((item) => (
                                <TableRow
                                    key={item.id}
                                    data-testid={`${testidPrefix}-row-${item.id}`}
                                >
                                    {columns.map((c) => (
                                        <TableCell key={c.key}>
                                            {c.render
                                                ? c.render(item)
                                                : String(item[c.key] ?? "")}
                                        </TableCell>
                                    ))}
                                    <TableCell className="text-right">
                                        <div className="inline-flex gap-1">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => openEdit(item)}
                                                data-testid={`${testidPrefix}-edit-${item.id}`}
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setDeleteTarget(item)}
                                                className="text-destructive hover:text-destructive"
                                                data-testid={`${testidPrefix}-delete-${item.id}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            {/* Edit / create sheet */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetContent
                    className="sm:max-w-2xl overflow-y-auto"
                    data-testid={`${testidPrefix}-sheet`}
                >
                    <SheetHeader>
                        <SheetTitle>
                            {editing ? "Edit" : "Add new"} {title.slice(0, -1) === title ? title : title.replace(/s$/, "")}
                        </SheetTitle>
                        <SheetDescription>
                            Fill in the fields below and save.
                        </SheetDescription>
                    </SheetHeader>
                    <form onSubmit={submit} className="space-y-5 py-6">
                        {fields.map((f) => (
                            <div key={f.name} className="space-y-2">
                                <Label htmlFor={f.name}>
                                    {f.label}
                                    {f.required && (
                                        <span className="text-destructive ml-1">*</span>
                                    )}
                                </Label>
                                {f.help && (
                                    <p className="text-xs text-slate-500">{f.help}</p>
                                )}
                                <FieldRenderer
                                    field={f}
                                    value={formData[f.name]}
                                    onChange={setField}
                                    testidPrefix={testidPrefix}
                                />
                            </div>
                        ))}
                        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setSheetOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={saving}
                                className="bg-slate-950 hover:bg-slate-800 text-white"
                                data-testid={`${testidPrefix}-save-button`}
                            >
                                {saving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : editing ? (
                                    "Save changes"
                                ) : (
                                    "Create"
                                )}
                            </Button>
                        </div>
                    </form>
                </SheetContent>
            </Sheet>

            {/* Delete confirm */}
            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(o) => !o && setDeleteTarget(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this item?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={remove}
                            className="bg-destructive hover:bg-destructive/90"
                            data-testid={`${testidPrefix}-delete-confirm`}
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// Re-export a status badge utility for pages
export function ActiveBadge({ active }) {
    return active ? (
        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
            Active
        </Badge>
    ) : (
        <Badge variant="outline" className="text-slate-500">
            Hidden
        </Badge>
    );
}

export function ViewIcon() {
    return <Eye className="w-4 h-4" />;
}
